import { IconMenu2, IconSearch } from '@tabler/icons-solidjs'
import { createSignal, For, type JSX, Show, splitProps } from 'solid-js'
import { cn } from '../lib/cn.ts'
import { tv } from '../lib/tv.ts'
import { Kbd } from './kbd.tsx'
import { Sheet } from './sheet.tsx'

/**
 * The application chrome: a fixed sidebar, a topbar, and a scrolling work area.
 *
 * Why a shell at all — the single biggest reason the first passes read as "a web page with
 * components on it" rather than as a tool is that there was no persistent frame. An operator
 * moving between Склад and Виробництво forty times a shift needs the furniture to stay put.
 *
 * Why a LIGHT sidebar: a quiet neutral layer keeps the application frame present without drawing
 * a box around every workspace. The active row uses the soft brand surface, leaving the full
 * accent fill for the primary action in the current task.
 *
 * Router-free by construction. Items carry an `href` and an `active` flag the app computes, so
 * `@veem/ui` never imports `@solidjs/router` and apps/shopfloor can reuse this with its own
 * navigation model.
 */
const shell = tv({
  slots: {
    /* The chrome is OUTSIDE the scroll container, not sticky inside it. Two reasons: a sticky
       header carrying `backdrop-filter` forces a compositing layer that repaints on every scroll
       frame — visibly, on a long page — and a scroll region that owns its own overflow is what
       lets a table header stick to the top of the work area rather than to the viewport. */
    root: 'grid h-dvh overflow-hidden bg-app text-text lg:grid-cols-[var(--size-sidebar)_minmax(0,1fr)]',
    sidebar: 'hidden h-full min-h-0 flex-col border-r border-border bg-surface-muted lg:flex',
    brand: 'flex h-(--size-topbar) shrink-0 items-center gap-2.5 border-b border-border px-4',
    brandMark:
      'grid size-8 shrink-0 place-items-center rounded-card bg-ink text-micro font-bold text-text-inverse',
    brandName: 'font-display text-body font-bold tracking-[-0.025em] text-text',
    nav: 'flex-1 overflow-y-auto px-2 py-3',
    group:
      'px-2 pt-4 pb-1 text-micro font-semibold tracking-[0.1em] text-text-subtle uppercase first:pt-0',
    item: [
      'flex items-center gap-2.5 rounded-control px-2.5 py-2 text-body text-text-muted max-md:min-h-(--size-touch)',
      'transition-colors duration-(--duration-press) ease-out',
      'hover:bg-surface-sunken hover:text-text',
      'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent',
      '[&_svg]:size-4 [&_svg]:shrink-0',
    ],
    itemActive: 'bg-accent-soft font-semibold text-text hover:bg-accent-soft hover:text-text',
    /* A module the current phase has not built yet. Shown rather than hidden: the sidebar is the
       clearest statement of what the finished system covers, and hiding future modules makes a
       phased delivery look like a thin one. */
    itemPending: 'cursor-default text-text-subtle hover:bg-transparent hover:text-text-subtle',
    itemLabel: 'min-w-0 flex-1 truncate',
    itemCount: 'shrink-0 font-mono text-micro text-text-subtle tabular-nums',
    itemPartial: 'shrink-0 rounded-control border border-border px-1 text-micro text-text-subtle',
    sidebarFooter: 'shrink-0 border-t border-border p-2',
    navTrigger: [
      'grid size-(--size-control-sm) shrink-0 place-items-center rounded-control lg:hidden',
      'text-text-muted transition-colors duration-(--duration-press) ease-out',
      'hover:bg-surface-sunken hover:text-text',
      'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent',
    ],
    main: 'flex min-h-0 min-w-0 flex-col',
    topbar: [
      'flex h-(--size-topbar) shrink-0 items-center gap-3',
      'border-b border-border bg-surface px-4',
    ],
    heading: 'min-w-0 flex-1',
    title: 'truncate text-ui font-semibold tracking-tight text-text',
    breadcrumb: 'truncate text-micro text-text-subtle',
    topbarActions: 'flex shrink-0 items-center gap-2',
    search: [
      'hidden items-center gap-2 rounded-control border border-border bg-surface-muted',
      'h-(--size-control-sm) pr-1.5 pl-2.5 text-body text-text-subtle md:flex',
      'transition-colors duration-(--duration-press) ease-out',
      'hover:border-border-strong hover:text-text-muted',
      'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent',
    ],
    content: 'min-w-0 flex-1 overflow-x-hidden overflow-y-auto p-4 lg:p-6',
  },
})

export interface NavItem {
  href: string
  label: string
  icon?: JSX.Element
  /** A queue depth, an unread count. Omit when zero rather than rendering `0`. */
  count?: string
  /**
   * The screen is reachable but the module behind it is only partly delivered. Distinct from
   * `pending`, which is a module with no screen at all: hiding a working screen to signal an
   * unfinished module costs the user the part that does work.
   */
  partial?: boolean
  active?: boolean
  /** Renders the row as inert — a module scheduled for a later phase. */
  pending?: boolean
}

export interface NavGroup {
  label: string
  items: NavItem[]
}

export interface AppShellProps {
  productName: string
  /** Two or three letters for the mark. Ukrainian initials are fine. */
  productMark: string
  nav: NavGroup[]
  /** Page title in the topbar. */
  title: string
  breadcrumb?: string
  /** Page-level actions, rightmost in the topbar. */
  actions?: JSX.Element
  /** Opens the command palette. Omit and the search affordance is not rendered. */
  onOpenCommands?: () => void
  /** Rendered at the bottom of the sidebar — the account row, sign-out. */
  sidebarFooter?: JSX.Element
  children: JSX.Element
}

export const AppShell = (props: AppShellProps) => {
  const [local] = splitProps(props, [
    'productName',
    'productMark',
    'nav',
    'title',
    'breadcrumb',
    'actions',
    'onOpenCommands',
    'sidebarFooter',
    'children',
  ])
  const styles = shell()
  const [isNavOpen, setIsNavOpen] = createSignal(false)

  /* Rendered twice — in the permanent sidebar and in the mobile sheet. Below `lg` the sidebar is
     `display:none`, so without the sheet a phone can reach a module and then has no way out of
     it. One markup source, so the two can never drift. */
  const navSections = (onNavigate?: () => void) => (
    <For each={local.nav}>
      {(group) => (
        <>
          <p class={styles.group()}>{group.label}</p>
          <For each={group.items}>
            {(item) => (
              <Show
                when={!item.pending}
                fallback={
                  <span class={cn(styles.item(), styles.itemPending())} aria-disabled="true">
                    <Show when={item.icon}>{(icon) => icon()}</Show>
                    <span class={styles.itemLabel()}>{item.label}</span>
                    <Show when={item.count}>
                      {(count) => <span class={styles.itemCount()}>{count()}</span>}
                    </Show>
                  </span>
                }
              >
                <a
                  href={item.href}
                  class={cn(styles.item(), item.active && styles.itemActive())}
                  aria-current={item.active ? 'page' : undefined}
                  onClick={() => onNavigate?.()}
                >
                  <Show when={item.icon}>{(icon) => icon()}</Show>
                  <span class={styles.itemLabel()}>{item.label}</span>
                  <Show when={item.partial}>
                    <span class={styles.itemPartial()}>частково</span>
                  </Show>
                  <Show when={item.count}>
                    {(count) => <span class={styles.itemCount()}>{count()}</span>}
                  </Show>
                </a>
              </Show>
            )}
          </For>
        </>
      )}
    </For>
  )

  return (
    <>
      <div class={styles.root()}>
        <aside class={styles.sidebar()}>
          <div class={styles.brand()}>
            <span class={styles.brandMark()} aria-hidden="true">
              {local.productMark}
            </span>
            <span class={styles.brandName()}>{local.productName}</span>
          </div>

          <nav class={styles.nav()} aria-label="Модулі">
            {navSections()}
          </nav>

          <Show when={local.sidebarFooter}>
            {(footer) => <div class={styles.sidebarFooter()}>{footer()}</div>}
          </Show>
        </aside>

        <div class={styles.main()}>
          <header class={styles.topbar()}>
            <button
              type="button"
              class={styles.navTrigger()}
              aria-label="Відкрити меню"
              aria-expanded={isNavOpen()}
              onClick={() => setIsNavOpen(true)}
            >
              <IconMenu2 size={18} strokeWidth={1.5} aria-hidden="true" />
            </button>

            <div class={styles.heading()}>
              <Show when={local.breadcrumb}>
                {(breadcrumb) => <p class={styles.breadcrumb()}>{breadcrumb()}</p>}
              </Show>
              <h1 class={styles.title()}>{local.title}</h1>
            </div>

            <div class={styles.topbarActions()}>
              <Show when={local.onOpenCommands}>
                <button
                  type="button"
                  class={styles.search()}
                  onClick={() => local.onOpenCommands?.()}
                >
                  <IconSearch size={14} strokeWidth={1.5} class="shrink-0" aria-hidden="true" />
                  <span>Команди</span>
                  <Kbd>⌘K</Kbd>
                </button>
              </Show>
              <Show when={local.actions}>{(actions) => actions()}</Show>
            </div>
          </header>

          <main class={styles.content()}>{local.children}</main>
        </div>
      </div>

      {/* Outside the shell grid on purpose. As a grid child this wrapper claimed a row of its
          own — its only content is the sheet's fixed-position scrim, but the row was still
          tracked, and the whole application sat ~140px down the page on every phone. */}
      <div class="lg:hidden">
        <Sheet
          open={isNavOpen()}
          onOpenChange={setIsNavOpen}
          title={local.productName}
          description="Модулі системи"
          closeLabel="Закрити меню"
          bodyClass="p-2"
          icon={<span class="text-micro font-bold">{local.productMark}</span>}
          footer={local.sidebarFooter}
        >
          <nav aria-label="Модулі">{navSections(() => setIsNavOpen(false))}</nav>
        </Sheet>
      </div>
    </>
  )
}
